import React from 'react'
import { Box } from '@mui/material'
import { AmchartSeeker, CardSeeker } from '@ui-seeker/core'
import { analyzerChartVisibility, isAnalyzerLoading } from '../../@signals'
import { NoDataFound, AppCardTitle, AppResultsSkeleton } from "@components"
import AnalyzerPricesChartActions from 'Pages/AnalyzersV2/@components/PricesChart/Toolbar'
import useAnalyzerChart from 'Pages/AnalyzersV2/@components/PricesChart/@hooks/useChart'
import FullScreenChartModal from "./FullScreenModal";
import AnalyzerExecutionInfo from "../ExecutionInfo"
import {currencyCode, reports, searchID} from "@signals";
import { chartEvidence } from "Pages/AnalyzersV2/@services/askEvidence"
import { analyzerAskContext } from "Pages/AnalyzersV2/@services/askScope"
import { orderMainLast } from "Pages/AnalyzersV2/@services/mainSeries"
import { mainSeriesLook } from "./mainSeriesLook"


export default function AnalyzerPricesChart(
	{
		chartData,
		keySeries,
		xTitle,
		isDateAxis,
		isRating = false,
		sitesFilterOptions,
		officeData,
		executionDateTime,
		mainKeys = [],
		...props
	}) {

	const {
		showByPrice,
		onChangeShowPrice,
		filteredChart,
		seriesToApply,
		isLoading,
		handleIsLoading,
		titleChart,
		hasSeriesDiff,
		onFilterSites,
		sitesSelected,
		onLegendClick,
		setIsVisible,
		showChartInFullScreen,
		toggleFullScreen,
	} = useAnalyzerChart(keySeries, chartData, sitesFilterOptions)

	// the main provider's / main site's line: always drawn, last so it is on top, thick and in the app blue (CU-869fmm949)
	const series = orderMainLast(seriesToApply, mainKeys)

	// "Ask Iris about this chart" (CU-869fkdhjv phase 3): from what is painted — `filteredChart` (site filter),
	// the active mode's series, the visible ones — handed in from here, never recomputed (plan-review Obs. 4)
	const getAsk = () => {
		const ctx = analyzerAskContext()
		return chartEvidence({
			section: ctx.section, title: titleChart, mode: showByPrice,
			siteFilter: sitesFilterOptions?.find?.(o => o.value === sitesSelected)?.label || null,
			data: filteredChart, series, visible: analyzerChartVisibility.value, mainKeys,
			currency: currencyCode.value, labels: ctx.labels,
			params: ctx.params, freshness: ctx.freshness, dsRepricing: ctx.dsRepricing,
		})
	}

	if (isAnalyzerLoading.value || isLoading) return <AppResultsSkeleton chart height={500}/>
	if (!chartData?.length) return <></>
	if (!seriesToApply?.filter(({ dataKey }) => dataKey).length) {
		return <CardSeeker
			data-tour-id="tour-prices-chart"
			title={<AppCardTitle icon="chart-line" title="Chart"/>}
			actions={<AnalyzerPricesChartActions
			onChange={onChangeShowPrice}
			showByPrice={showByPrice}
			isLoading={isLoading}
			handleIsLoading={handleIsLoading}
			isRating={isRating}
			hasSeriesDiff={hasSeriesDiff}
			onFilterSites={onFilterSites}
			sitesSelected={sitesSelected}
			setIsVisible={setIsVisible}
			toggleFullScreen={toggleFullScreen}
		/>}>
			<NoDataFound sx={{ span: { margin: '0', width: '400px' } }}/>
		</CardSeeker>
	}

	return <>
		<AmchartSeeker
			rootID="ChartSelected"
			title={<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start', gap: 2 }}>
				<AppCardTitle icon="chart-line" title={titleChart}/>
				{!searchID.value && !!reports.value && <AnalyzerExecutionInfo officeData={officeData} executionDateTime={executionDateTime}/>}
			</Box>}
			data={filteredChart}
			series={series.map(({ dataKey, color, isMain }) => ({
				name: dataKey,
				yField: dataKey,
				xField: 'date',
				color: color,
				connect: true,
				hidden: !analyzerChartVisibility.value.includes(dataKey),
				tooltip: {
					pointerOrientation: "horizontal",
				},
				tension: 1,
				type: (titleChart !== 'Dates of Rental' && titleChart !== "Length of Rental") ? 'column' : 'line',
				...mainSeriesLook(isMain),
			}))}
			sx={{ height: 600 }}
			cardProps={{
				'data-tour-id': 'tour-prices-chart',
				actions: <AnalyzerPricesChartActions
					onChange={onChangeShowPrice}
					showByPrice={showByPrice}
					isLoading={isLoading}
					handleIsLoading={handleIsLoading}
					isRating={isRating}
					hasSeriesDiff={hasSeriesDiff}
					onFilterSites={onFilterSites}
					sitesSelected={sitesSelected}
					sitesFilterOptions={sitesFilterOptions}
					setIsVisible={setIsVisible}
					toggleFullScreen={toggleFullScreen}
					getAsk={isRating ? null : getAsk}
				/>
			}}
			chartProps={{
				showBullets: true,
				enabledPDF: false,
				numberFormat: `#,###.00 ' ${showByPrice !== "price" ? '%' : ""}'`,
				dateFormatReceived: 'yyyyMMdd',
				scrollbarY: {
					show: true
				},
				xAxis: {
					categoryField: 'date',
					maxDeviation: 0.3,
					isDateAxis: isDateAxis,
					dateFormat: 'dd MMM yyyy (EEE)',
					title: xTitle,
					baseInterval: {
						timeUnit: 'day',
						count: 1
					},
					labelProps: {
						rotation: filteredChart.some(({ date }) => date.length > 8) ? "10" : "0"
					},
					...props.labelProps
				},
				legend: {
					onClick: onLegendClick
				},
				yAxis: {
					title: isRating ? 'Rating' : showByPrice === "price" ? 'Prices' : "Variation"
				},
			}}
		/>

	<FullScreenChartModal
		titleChart={titleChart}
		officeData={officeData}
		executionDateTime={executionDateTime}
		filteredChart={filteredChart}
		isOpen={showChartInFullScreen}
		toggleOpen={toggleFullScreen}
		showByPrice={showByPrice}
		seriesToApply={series}
		isDateAxis={isDateAxis}
		xTitle={xTitle}
		isRating={isRating}
		{...props}
	/>
	</>
}